import { GQL } from "@binale-tech/shared";

import type { MessageId } from "@domain/intl/useTranslation";
import { DmsType } from "@domain/models/dms";

import { withThinking } from "../runner/runAgent";
import type { AgentDetailGroup, AgentDetailItem, AgentStep, ReportLine } from "../runner/types";
import { isInYear } from "./inYear";

const EXCLUDED_TYPES: string[] = [DmsType.new_documents, DmsType.trash];

const Fill = GQL.IDmsFillContactOutcome;
const Create = GQL.IDmsCreateContactsOutcome;

const fillDetails: Record<GQL.IDmsFillContactOutcome, [AgentDetailGroup, MessageId]> = {
    [Fill.Linked]: ["done", "app.agent.details.documentContacts.linked"],
    [Fill.AlreadyLinked]: ["untouched", "app.agent.details.documentContacts.alreadyLinked"],
    [Fill.NoMatch]: ["untouched", "app.agent.details.documentContacts.noMatch"],
    [Fill.NoAiResult]: ["untouched", "app.agent.details.documentContacts.noAiResult"],
    [Fill.NotFound]: ["untouched", "app.agent.details.documentContacts.notFound"],
};

const createDetails: Record<GQL.IDmsCreateContactsOutcome, [AgentDetailGroup, MessageId]> = {
    [Create.CreatedAndLinked]: ["done", "app.agent.details.documentContacts.created"],
    [Create.LinkedToExisting]: ["done", "app.agent.details.documentContacts.linked"],
    [Create.AlreadyLinked]: ["untouched", "app.agent.details.documentContacts.alreadyLinked"],
    [Create.NotFound]: ["untouched", "app.agent.details.documentContacts.notFound"],
    [Create.NoVatId]: ["untouched", "app.agent.details.documentContacts.noVatId"],
    [Create.NotInVatRegister]: ["untouched", "app.agent.details.documentContacts.notInVatRegister"],
    [Create.OwnCompanyVat]: ["untouched", "app.agent.details.documentContacts.ownCompanyVat"],
};

// an outcome this client does not know yet gets no row
const detail = (
    result: { documentId: string; contactId?: string | null },
    mapped: [AgentDetailGroup, MessageId] | undefined,
): AgentDetailItem[] =>
    mapped
        ? [
              {
                  group: mapped[0],
                  ref: { kind: "document", id: result.documentId },
                  ...(result.contactId ? { target: { kind: "contact" as const, id: result.contactId } } : {}),
                  outcome: { textId: mapped[1] },
              },
          ]
        : [];

export const documentContactsStep: AgentStep = {
    id: "documentContacts",
    product: [GQL.IProductKey.Dms, GQL.IProductKey.Contacts],
    async run(ctx, io) {
        const ids = [...ctx.documents().values()]
            .filter(
                (doc) => !EXCLUDED_TYPES.includes(doc.type) && !doc.contactId && isInYear(doc.documentDate, ctx.year),
            )
            .map((doc) => String(doc.key));
        if (ids.length === 0) return { status: "nothing" };

        const learnMoreId = "app.agent.documentContacts.learnMore" as const;
        const linkApproved = await io.approve({
            textId: "app.agent.documentContacts.approveLink.question",
            values: { count: ids.length },
            goLabelId: "app.agent.documentContacts.approveLink.go",
            learnMoreId,
        });
        const fillResults = linkApproved
            ? await withThinking(io, "app.agent.documentContacts.thinking", () => ctx.fillDocumentContacts(ids))
            : [];
        // the last outcome per document wins: a create result replaces the fill result
        const outcomes = new Map<string, AgentDetailItem[]>(
            fillResults.map((r) => [r.documentId, detail(r, fillDetails[r.outcome])]),
        );
        const filled = new Set(fillResults.filter((r) => r.outcome === Fill.Linked).map((r) => r.documentId));
        const rest = ids.filter((id) => !filled.has(id));

        let createApproved = false;
        let created = 0;
        if (rest.length > 0) {
            createApproved = await io.approve({
                textId: "app.agent.documentContacts.approveCreate.question",
                values: { count: rest.length },
                goLabelId: "app.agent.documentContacts.approveCreate.go",
                learnMoreId,
            });
            if (createApproved) {
                const summary = await withThinking(io, "app.agent.documentContacts.thinking", () =>
                    ctx.createDocumentContacts(rest),
                );
                created = summary.createdContacts.length;
                for (const r of summary.documents) {
                    outcomes.set(r.documentId, detail(r, createDetails[r.outcome]));
                }
            }
        }
        const linked = filled.size;
        if (!linkApproved && !createApproved) return { status: "skipped", byUser: true };
        const details = ids.flatMap((id) => outcomes.get(id) ?? []);
        if (linked === 0 && created === 0) return { status: "nothing", details };

        io.post({ kind: "done", textId: "app.agent.documentContacts.done", values: { linked, created }, details });
        const summary: ReportLine[] = [];
        if (linked > 0) summary.push({ textId: "app.agent.report.documentContactsLinked", values: { count: linked } });
        if (created > 0)
            summary.push({ textId: "app.agent.report.documentContactsCreated", values: { count: created } });
        return { status: "done", summary, details };
    },
};
